import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, DollarSign, Plus, AlertCircle, CreditCard, Tag, ArrowLeft, Loader2 } from 'lucide-react';

import { ROUTES } from '../../constants/routes';

import { AddPaymentDrawer } from '../../components/billing/AddPaymentDrawer';
import { CreateSubscriptionModal } from '../../components/billing/CreateSubscriptionModal';
import { CreateDiscountCodeModal } from '../../components/billing/CreateDiscountCodeModal';
import BillingSettingsPage from './BillingSettings';

import { Button } from '../../components/ui/Button';
import {
  BillingCard,
  BillingCardHeader,
  BillingCardTitle,
  BillingCardContent,
} from '../../components/billing/BillingCard';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useOutstandingPayments } from '../../hooks/useOutstandingPayments';
import { usePayments } from '../../hooks/usePayments';
import { useCompanySubscriptions } from '../../hooks/useCompanySubscriptions';
import type { CompanySubscription } from '../../hooks/useCompanySubscriptions';
import { useSaaSSubscription } from '../../hooks/useSaaSSubscription';
import { useDiscountCodes } from '../../hooks/useDiscountCodes';
import type { DiscountCode } from '../../hooks/useDiscountCodes';

function getSubscriptionTypeLabel(type: string): string {
  switch (type) {
    case 'recurring':
      return 'Recurring';
    case 'punch_card':
      return 'Punch Card';
    case 'trial':
      return 'Trial';
    case 'drop_in':
      return 'Drop-In';
    default:
      return type;
  }
}

function getDiscountLabel(code: DiscountCode): string {
  if (code.discount_type === 'percent') {
    return `${code.discount_value}% off`;
  }
  return `$${code.discount_value.toFixed(2)} off`;
}

function getDurationLabel(code: DiscountCode): string {
  if (code.duration_type === 'forever') return 'Forever';
  if (code.duration_type === 'once') return 'First payment';
  if (code.duration_type === 'repeating' && code.duration_months) {
    return `${code.duration_months} months`;
  }
  return code.duration_type;
}

function getRollOpsPlanLabel(subscription: {
  plan_type: string;
  status?: string;
  billing_end_date: string | null;
  trial_end_date?: string | null;
} | null): string {
  if (!subscription) return 'RollOps Plan';
  const planLabel = subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1);
  const base = `RollOps ${planLabel} Plan`;
  const formatDate = (d: string) =>
    d.length >= 10 ? `${d.slice(5, 7)}-${d.slice(8, 10)}-${d.slice(0, 4)}` : d;
  if (subscription.status === 'trialing' && subscription.trial_end_date) {
    return `${base} - Trial ends ${formatDate(subscription.trial_end_date)}`;
  }
  if (subscription.billing_end_date) {
    return `${base} - Renews ${formatDate(subscription.billing_end_date)}`;
  }
  return base;
}

/**
 * BillingUnified - Simplified billing page without contextual tabs.
 * Header shows "Billing & Subscriptions" with a gear icon that navigates to settings.
 * Hero section: Manual Payments split into Outstanding (left) and Recent Payments (right).
 */
export default function BillingUnified() {
  const navigate = useNavigate();
  const { data: companyId, role } = useCurrentCompany();
  const isOwner = role === 'owner';
  const { data: familyGroups, isLoading: isLoadingOutstanding } = useOutstandingPayments({
    companyId: companyId ?? null,
    enabled: !!companyId && isOwner,
  });
  const {
    data: paymentsData,
    isLoading: isLoadingPayments,
  } = usePayments({ limit: 10, enabled: !!companyId });
  const payments = paymentsData?.payments ?? [];
  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
  } = useCompanySubscriptions();
  const {
    data: discountCodes,
    isLoading: isLoadingDiscountCodes,
  } = useDiscountCodes();
  const { data: saasSubscription } = useSaaSSubscription();
  const [showSettings, setShowSettings] = useState(false);
  const [showAddPaymentDrawer, setShowAddPaymentDrawer] = useState(false);
  const [showCreateSubscriptionModal, setShowCreateSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<CompanySubscription | null>(null);
  const [editingDiscountCode, setEditingDiscountCode] = useState<DiscountCode | null>(null);
  const [showCreateDiscountCodeModal, setShowCreateDiscountCodeModal] = useState(false);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page Header - matches ContentArea styling, gear aligned right */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">
              {showSettings ? 'Billing Settings' : 'Billing & Subscriptions'}
            </h1>
            <p className="text-white/80 mt-1">
              {showSettings ? 'Manage your RollOps subscription and billing preferences' : 'Manage payments, subscriptions, and discount codes'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {showSettings ? (
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Billing
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.SUBSCRIPTIONS_PLANS_ROLLOPS)}
                  className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] hover:underline cursor-pointer"
                >
                  {getRollOpsPlanLabel(saasSubscription ?? null)}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="p-2 rounded-lg text-text-tertiary hover:text-text-secondary hover:bg-white/5 transition-colors inline-flex"
                  aria-label="RollOps subscription settings"
                >
                  <Settings className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className={`flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 ${showSettings ? 'pt-2 pb-6' : 'py-6'}`}
      >
        {showSettings ? (
          <BillingSettingsPage />
        ) : (
          <>
        {/* Section B: Outstanding (left) and Recent Payments (right) as separate cards */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Left: Outstanding Payments card */}
          <BillingCard className="flex-1 min-w-0 md:max-w-[460px]">
            <BillingCardContent>
              <BillingCardHeader>
                <BillingCardTitle>
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Outstanding
                </BillingCardTitle>
              </BillingCardHeader>
              <div className="overflow-y-auto scrollbar-styled max-h-[265px] min-h-[120px]">
                {isLoadingOutstanding ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-[#9B1421]" />
                  </div>
                ) : !isOwner ? (
                  <p className="text-sm text-[#A9A3A3] py-4 px-4 text-center">
                    Sign in as owner to view outstanding payments
                  </p>
                ) : familyGroups && familyGroups.length > 0 ? (
                  familyGroups.map((group) => (
                    <div
                      key={group.billing_account_id}
                      className="w-full flex items-center justify-between py-2 px-4 md:px-6 border-b border-[#504A4A]/30 last:border-b-0 text-left"
                    >
                      <span className="text-sm text-[#EBFFEE]">
                        Family ({group.family_lead_name || 'Unknown'})
                      </span>
                      <span className="text-sm font-medium text-[#9B1421]">
                        ${group.current_due.toFixed(2)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-[#A9A3A3] py-4 px-4 text-center">
                    No outstanding payments at this time
                  </p>
                )}
              </div>
            </BillingCardContent>
          </BillingCard>

          {/* Right: Recent Payments card */}
          <BillingCard className="flex-[2] min-w-0">
            <BillingCardContent>
              <BillingCardHeader>
                <BillingCardTitle>
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.BILLING_PAYMENTS)}
                    className="flex items-center gap-2 text-[#EBFFEE] hover:opacity-90 hover:underline transition-opacity cursor-pointer"
                  >
                    <DollarSign className="w-4 h-4 text-white" />
                    Recent Payments
                  </button>
                </BillingCardTitle>
                <Button
                  size="sm"
                  disabled
                  className="bg-[#363232] text-[#A9A3A3] shrink-0 cursor-not-allowed opacity-60"
                  onClick={() => setShowAddPaymentDrawer(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Payment
                </Button>
              </BillingCardHeader>
              <div className="overflow-x-auto overflow-y-auto scrollbar-styled max-h-[200px] min-h-[120px]">
                <table className="w-full">
                  <thead className="sticky top-0 bg-[#221F1F] z-10">
                    <tr className="border-b border-[#504A4A]">
                      <th className="text-left py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3]">
                        Member
                      </th>
                      <th className="text-left py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden sm:table-cell">
                        Subscription
                      </th>
                      <th className="text-right py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3]">
                        Amount
                      </th>
                      <th className="text-left py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden md:table-cell">
                        Status
                      </th>
                      <th className="text-left py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden md:table-cell">
                        Method
                      </th>
                      <th className="text-left py-2 px-4 md:px-6 text-sm font-medium text-[#A9A3A3] hidden lg:table-cell">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingPayments ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center">
                          <Loader2 className="w-6 h-6 animate-spin text-[#9B1421] mx-auto" />
                        </td>
                      </tr>
                    ) : payments && payments.length > 0 ? (
                      payments.map((payment) => (
                        <tr
                          key={payment.id}
                          className={`border-b border-[#504A4A]/50 last:border-b-0 hover:bg-white/[0.02] transition-colors ${
                            payment.status === 'failed' ? 'bg-red-950/20' : ''
                          }`}
                        >
                          <td className="py-2 px-4 md:px-6 text-sm text-[#EBFFEE]">
                            {payment.memberName}
                          </td>
                          <td className="py-2 px-4 md:px-6 text-sm text-[#A9A3A3] hidden sm:table-cell">
                            {payment.subscriptionName}
                          </td>
                          <td className="py-2 px-4 md:px-6 text-right text-sm font-medium text-[#EBFFEE]">
                            ${payment.amount.toFixed(2)}
                          </td>
                          <td className="py-2 px-4 md:px-6 text-sm hidden md:table-cell">
                            {payment.status === 'failed' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/40 text-red-400" title={payment.declineReason}>
                                Failed
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400">
                                Paid
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-4 md:px-6 text-sm text-[#A9A3A3] hidden md:table-cell">
                            {payment.method}
                          </td>
                          <td className="py-2 px-4 md:px-6 text-sm text-[#A9A3A3] hidden lg:table-cell">
                            {payment.date}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-[#A9A3A3]">
                          No recent payments
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center pt-3 pb-4 border-t border-[#504A4A]/50 mt-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5"
                  onClick={() => navigate(ROUTES.BILLING_PAYMENTS)}
                >
                  View all
                </Button>
              </div>
            </BillingCardContent>
          </BillingCard>
        </div>

        {/* Section C: Utility Belt - Subscription Plans (left) | Discount Codes (right) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Left: Subscription Plans */}
          <BillingCard>
            <BillingCardContent>
              <BillingCardHeader>
                <BillingCardTitle>
                  <CreditCard className="w-4 h-4 text-white" />
                  Subscription Plans
                </BillingCardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE]"
                  onClick={() => {
                    setEditingSubscription(null);
                    setShowCreateSubscriptionModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Plan
                </Button>
              </BillingCardHeader>
              <div className="divide-y divide-[#504A4A]/50 max-h-[300px] overflow-y-auto scrollbar-styled">
                {isLoadingSubscriptions ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-[#9B1421]" />
                  </div>
                ) : subscriptions && subscriptions.length > 0 ? (
                  subscriptions.map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => {
                        setEditingSubscription(plan);
                        setShowCreateSubscriptionModal(true);
                      }}
                      className="w-full flex items-center justify-between py-3 px-4 hover:bg-white/[0.02] transition-colors text-left cursor-pointer"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#EBFFEE]">
                          {plan.name}
                        </p>
                        <p className="text-xs text-[#A9A3A3]">
                          {getSubscriptionTypeLabel(plan.subscription_type)}
                        </p>
                      </div>
                      <span className="text-sm font-medium text-[#EBFFEE]">
                        ${plan.price.toFixed(2)}/
                        {plan.billing_cycle === 'annual' ? 'yr' : 'mo'}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-[#A9A3A3] py-6 px-4 text-center">
                    No subscription plans yet
                  </p>
                )}
              </div>
            </BillingCardContent>
          </BillingCard>

          {/* Right: Discount Codes */}
          <BillingCard>
            <BillingCardContent>
              <BillingCardHeader>
                <BillingCardTitle>
                  <Tag className="w-4 h-4 text-white" />
                  Discount Codes
                </BillingCardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#504A4A] text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE]"
                  onClick={() => {
                    setEditingDiscountCode(null);
                    setShowCreateDiscountCodeModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Code
                </Button>
              </BillingCardHeader>
              <div className="divide-y divide-[#504A4A]/50 max-h-[280px] overflow-y-auto scrollbar-styled">
                {isLoadingDiscountCodes ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-[#9B1421]" />
                  </div>
                ) : discountCodes && discountCodes.length > 0 ? (
                  discountCodes.map((code) => (
                    <button
                      key={code.id}
                      type="button"
                      onClick={() => {
                        setEditingDiscountCode(code);
                        setShowCreateDiscountCodeModal(true);
                      }}
                      className="w-full flex items-center justify-between py-3 px-4 hover:bg-white/[0.02] transition-colors text-left cursor-pointer"
                    >
                      <div>
                        <p className="text-sm font-mono font-medium text-[#EBFFEE]">
                          {code.code}
                        </p>
                        <p className="text-xs text-[#A9A3A3]">
                          {getDiscountLabel(code)} · {getDurationLabel(code)}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-[#A9A3A3] py-6 px-4 text-center">
                    No discount codes yet
                  </p>
                )}
              </div>
            </BillingCardContent>
          </BillingCard>
        </div>
          </>
        )}
      </div>

      <AddPaymentDrawer
        isOpen={showAddPaymentDrawer}
        onClose={() => setShowAddPaymentDrawer(false)}
      />
      <CreateSubscriptionModal
        isOpen={showCreateSubscriptionModal}
        onClose={() => {
          setEditingSubscription(null);
          setShowCreateSubscriptionModal(false);
        }}
        companyId={companyId ?? undefined}
        subscription={editingSubscription}
        onSuccess={() => {
          setEditingSubscription(null);
          setShowCreateSubscriptionModal(false);
        }}
      />
      <CreateDiscountCodeModal
        isOpen={showCreateDiscountCodeModal}
        onClose={() => {
          setEditingDiscountCode(null);
          setShowCreateDiscountCodeModal(false);
        }}
        companyId={companyId ?? undefined}
        discountCode={editingDiscountCode}
        onSuccess={() => {
          setEditingDiscountCode(null);
          setShowCreateDiscountCodeModal(false);
        }}
      />
    </div>
  );
}
